import { api, API_BASE, getCsrfToken } from './client';
import type { Theme, Task, TaskComment } from '../types/project';
import type { Person } from '../types/people';

export function fetchThemes(): Promise<Theme[]> {
  return api.get<Theme[]>('/api/themes');
}

export function fetchTheme(id: string): Promise<Theme> {
  return api.get<Theme>(`/api/themes/${id}`);
}

export function createTheme(data: { name: string; description?: string; parent_theme_id?: string; order_index?: number }): Promise<{ id: string }> {
  return api.post<{ id: string }>('/api/themes', data);
}

export function updateTheme(id: string, data: Partial<{ name: string; description: string; parent_theme_id: string | null; order_index: number; archived_at: string | null }>): Promise<{ ok: boolean }> {
  return api.patch<{ ok: boolean }>(`/api/themes/${id}`, data);
}

export function deleteTheme(id: string): Promise<{ ok: boolean }> {
  return api.patch<{ ok: boolean }>(`/api/themes/${id}`, { archived_at: new Date().toISOString() });
}

export function fetchThemePeople(themeId: string): Promise<Person[]> {
  return api.get<Person[]>(`/api/themes/${themeId}/people`);
}

export function updateThemePeople(themeId: string, personIds: string[]): Promise<{ ok: boolean }> {
  return api.put<{ ok: boolean }>(`/api/themes/${themeId}/people`, { person_ids: personIds });
}

export function fetchTasks(themeId: string): Promise<Task[]> {
  return api.get<Task[]>(`/api/tasks?theme_id=${themeId}`);
}

export function fetchAllTasks(filters?: {
  assignee_id?: string;
  theme_id?: string;
  sub_theme_id?: string;
}): Promise<Task[]> {
  const params = new URLSearchParams();
  if (filters?.assignee_id) params.set('assignee_id', filters.assignee_id);
  if (filters?.theme_id) params.set('theme_id', filters.theme_id);
  if (filters?.sub_theme_id) params.set('sub_theme_id', filters.sub_theme_id);
  const qs = params.toString();
  return api.get<Task[]>(`/api/tasks/all${qs ? `?${qs}` : ''}`);
}

export function fetchSubtasks(themeId: string, parentTaskId: string): Promise<Task[]> {
  return api.get<Task[]>(`/api/tasks?theme_id=${encodeURIComponent(themeId)}&parent_task_id=${encodeURIComponent(parentTaskId)}`);
}

export function fetchTask(id: string): Promise<Task> {
  return api.get<Task>(`/api/tasks/${id}`);
}

export function createTask(data: {
  theme_id: string;
  parent_task_id?: string;
  title: string;
  description?: string;
  status?: string;
  priority?: string;
  assignee?: string;
  assignee_ids?: string[];
  due_date?: string;
  order_index?: number;
}): Promise<{ id: string }> {
  return api.post<{ id: string }>('/api/tasks', data);
}

export function updateTask(
  id: string,
  data: Partial<{
    title: string;
    description: string | null;
    status: string;
    priority: string;
    assignee: string | null;
    assignee_ids: string[];
    due_date: string | null;
    order_index: number;
  }>
): Promise<{ ok: boolean }> {
  return api.patch<{ ok: boolean }>(`/api/tasks/${id}`, data);
}

export function deleteTask(id: string): Promise<void> {
  return api.delete(`/api/tasks/${id}`);
}

export function exportTasks(): void {
  window.location.href = `${API_BASE}/api/tasks/export`;
}

export function batchDeleteTasks(ids: string[]): Promise<{ deleted: number }> {
  return api.post<{ deleted: number }>('/api/tasks/batch-delete', { ids });
}

export interface TaskImportResult {
  imported: number;
  errors: { row: number; message: string }[];
}

export async function importTasks(file: File): Promise<TaskImportResult> {
  const text = await file.text();
  const csrf = await getCsrfToken();
  const res = await fetch(`${API_BASE}/api/tasks/import`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'text/csv', 'X-CSRF-Token': csrf },
    body: text,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? 'Import failed');
  return data as TaskImportResult;
}

export function fetchTaskComments(taskId: string): Promise<TaskComment[]> {
  return api.get<TaskComment[]>(`/api/task-comments?task_id=${taskId}`);
}

export function addTaskComment(taskId: string, content: string): Promise<{ id: string; created_at: string }> {
  return api.post<{ id: string; created_at: string }>('/api/task-comments', { task_id: taskId, content });
}
